import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {UpdateArchivableParams} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExecutionPlan} from 'studies/progress/Types';
import {ExperimentMethodPlanREST} from 'studies/typed/shared/Types';
import {patchJSON} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export type SourcePMEIIdsByTemplatePMDVIdREST = Record<string, Array<string>>;
export interface LegacyBowlingLaneConditionREST {
  replicateInfos: Array<{
    replicateNumber: number;
    // Used to add experiment method instance associations for the new condition
    sourcePMEIIdsByTemplatePMDVId?: SourcePMEIIdsByTemplatePMDVIdREST;
  }>;
  methodPlans: Array<ExperimentMethodPlanREST>;
  name: string;
  description?: string;
}

export interface ProcedureRunMethodExecutionInstanceInputREST {
  templateMethodVersionId: string;
  conditionName: string;
  replicateNumber: number;
}

export interface ProcedureRunReplicateEdgeInputREST {
  source: ProcedureRunMethodExecutionInstanceInputREST;
  target: ProcedureRunMethodExecutionInstanceInputREST;
}

export interface ProcedureRunConditionREST {
  position: number;
  templateMethodVersionId: string;
  methodPlan: ExperimentMethodPlanREST;
  replicateInfos: Array<{
    replicateNumber: number;
    sourcePMEIIds: Array<string>;
  }>;
  name: string;
  description?: string;
}

export interface UpdateProcedureRunParams extends UpdateArchivableParams {
  id: string;
  description?: string;
  newLegacyBowlingLaneConditions?: Array<LegacyBowlingLaneConditionREST>;
  newConditions?: Array<ProcedureRunConditionREST>;
  confirmationMethodByParamVersionId?: Record<string, ProcedureParameterConfirmationMethod>;
  // Backend does not allow this field to be included unless STUDIES_ENABLE_SPLIT_POOL is enabled.
  conditionReplicateFlowchart?: Array<ProcedureRunReplicateEdgeInputREST>;
}

export function getNewConditionCount(update: UpdateProcedureRunParams): number {
  return update.newConditions?.length ?? update.newLegacyBowlingLaneConditions?.length ?? 0;
}

export interface TaskResponse extends TaskRunner.OngoingTaskResponse {
  taskId: string;
  timingMeta: ExecutionPlan;
}

export interface UpdateProcedureRunResponse {
  updatedIds: Array<string>;
}

export async function updateProcedureRun(
  procedureRun: UpdateProcedureRunParams,
  {supportsAsync}: {supportsAsync: boolean}
): Promise<
  | {async: false; data: UpdateProcedureRunResponse}
  | {async: true; data: TaskResponse; taskMeta: TaskRunner.TaskMeta}
> {
  const updates = [procedureRun];
  const url = `${getApiBaseForObjectType(DomainGraphType.PROCEDURE_RUN)}?supportsAsync=${
    supportsAsync ? 1 : 0
  }`;
  // We use legacy patchJSON because net.patchJSONPromise / TaskRunner.runTask are too limited: we need to
  // inspect the response status code / headers since our response is dynamic and may either be a
  // synchronous web request or an asynchronous celery task, where our UX will reflect the asynchrony.
  const xhr: JQueryXHR = patchJSON({
    url,
    data: {updates},
  }) as JQueryXHR;
  const response = await Promise.resolve(xhr);
  if (xhr.status === 202) {
    return {
      async: true,
      data: response as TaskResponse,
      taskMeta: TaskRunner.getTaskMeta(xhr, url),
    };
  } else {
    return {
      async: false,
      data: response as UpdateProcedureRunResponse,
    };
  }
}
